// TOREFACTO : Use KdsDataTable component instead
import {
  KdsButton,
  KdsLink,
  KdsText,
  KdsTooltip,
} from "@kazaar-platform/design-system";
import { RiExpandDiagonal2Line } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { sumBy } from "lodash";
import { useRef } from "react";

import { getUniqueAddressesFromOrderItem } from "@/helpers/getUniqueAddressesFromOrderItem";
import { useIsHorizontallyScrollable } from "@/hooks/useIsHorizontallyScrollable";

import { type DeliverySummaryTableVersionsProps } from "./DeliverySummaryTableVersions.types";

export const DeliverySummaryTableVersions = ({
  orderItem,
  onTableVersionExpand,
}: DeliverySummaryTableVersionsProps) => {
  const { t } = useTranslate();

  const versions = orderItem.sandboxItem.SandboxItemVersion;
  const uniqueAddresses = getUniqueAddressesFromOrderItem(orderItem);

  const addressesAreaRef = useRef<HTMLDivElement>(null);

  const totalQuantity = sumBy(versions, (version) =>
    sumBy(version.SandboxItemVersionDispatch, "quantity")
  );

  const isAddressesScrollable = useIsHorizontallyScrollable(addressesAreaRef, [
    uniqueAddresses,
  ]);

  return (
    <div className="flex flex-col gap-sp-xs">
      {/* Table Versions */}
      <div className="flex w-full">
        {/* Version Column */}
        <div className="min-w-min max-w-fit border-r-bw-small border-outlined-border">
          <div className="flex h-si-xl items-center self-stretch rounded-tl-lg bg-surface-second px-sp-md">
            <KdsText size="subtitle-xs" color="text-bodyvariant">
              {t("tableVersions.headerVersion")}
            </KdsText>
          </div>
          <div className="flex h-8 items-center self-stretch border-y-bw-small border-outlined-border bg-surface-third px-sp-md">
            <KdsText
              size="caption-sm"
              color="text-bodyvariant"
              truncate
              dataCy="tableVersionTotalQuantity"
            >
              {t("tableVersions.totalQuantity", {
                quantity: totalQuantity,
              })}
            </KdsText>
          </div>
          {versions.map((version) => (
            <div
              key={version.id}
              className="flex h-si-xl items-center gap-sp-xs self-stretch border-b-bw-small border-outlined-border bg-white px-sp-md"
            >
              <KdsText size="subtitle-sm">{version.name}</KdsText>
            </div>
          ))}
        </div>

        {/* Addresses Columns */}
        <div
          ref={addressesAreaRef}
          className="flex w-max min-w-[125px] flex-1 overflow-y-auto"
        >
          {/* Empty column to manage right align */}
          <div className="min-w-0 flex-1">
            <div className="h-si-xl bg-surface-second px-sp-md" />
            <div className="h-8 border-y-bw-small border-outlined-border bg-surface-third px-sp-md" />
            {versions.map((version) => (
              <div
                key={version.id}
                className="h-si-xl border-b-bw-small border-outlined-border bg-white px-sp-md"
              />
            ))}
          </div>

          <div className="flex flex-col">
            <div className="flex justify-evenly">
              {/* Addresses  */}
              {uniqueAddresses?.map((address, index) => (
                <div
                  key={address.id}
                  className="w-[102px] min-w-[102px] overflow-hidden last:rounded-r-lg"
                >
                  <div className="flex h-si-xl items-center justify-center self-stretch bg-surface-second px-sp-md">
                    <KdsTooltip alt={address.nameAddress} placement="top">
                      <div className="truncate">
                        <KdsLink isUnderline={false}>
                          <KdsText
                            size="subtitle-xs"
                            color="text-bodyvariant"
                            truncate
                          >
                            {address.nameAddress}
                          </KdsText>
                        </KdsLink>
                      </div>
                    </KdsTooltip>
                  </div>
                  <div className="flex h-8 items-center justify-end self-stretch border-y-bw-small border-outlined-border bg-surface-third px-sp-md">
                    <KdsText
                      size="caption-sm"
                      color="text-bodyvariant"
                      dataCy={`tableVersionAddress${index + 1}TotalQuantity`}
                    >
                      {address.quantity}
                    </KdsText>
                  </div>
                  {versions.map((version) => (
                    <div
                      key={version.id}
                      className="flex h-si-xl items-center justify-end border-b-bw-small border-outlined-border bg-white px-sp-md"
                    >
                      <KdsText size="body-md">
                        {version.SandboxItemVersionDispatch[index]?.quantity ??
                          0}
                      </KdsText>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Expand Button */}
      {onTableVersionExpand && isAddressesScrollable && (
        <KdsButton
          size="small"
          variant="outlined"
          color="neutral"
          iconLeft={RiExpandDiagonal2Line}
          onPress={onTableVersionExpand}
        >
          {t("buttons.expand")}
        </KdsButton>
      )}
    </div>
  );
};
